/**
 * TopNav — UX §3.15. Sticky, hairline-bottom-border that thickens from 0
 * to 1px once scrolled > 12px. Wordmark "Connoisseur" in serif small-caps,
 * household switcher, profile menu.
 *
 * Generous height, NO shadow.
 */
import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SignOut, MagnifyingGlass } from '@phosphor-icons/react';
import { Avatar } from './Avatar';
import { HouseholdSwitcher } from './HouseholdSwitcher';
import { DropdownMenu } from './DropdownMenu';
import { cn } from '../../lib/cn';
import { useAuth } from '../../lib/auth';
import { useLogout } from '../../lib/queries';

function useScrolled(threshold = 12) {
  const [scrolled, setScrolled] = React.useState(false);
  React.useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > threshold);
    }
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [threshold]);
  return scrolled;
}

export function TopNav() {
  const scrolled = useScrolled();
  const { account } = useAuth();
  const logout = useLogout();
  const navigate = useNavigate();

  async function onLogout() {
    try {
      await logout.mutateAsync();
    } finally {
      navigate('/auth/request');
    }
  }

  return (
    <header
      className={cn(
        'sticky top-0 z-40',
        'bg-bg/95 backdrop-blur-sm',
        'transition-[border-color] duration-200 ease-out-slow',
        scrolled ? 'border-b border-ink-200' : 'border-b border-transparent',
      )}
    >
      <div className="mx-auto max-w-content w-full flex items-center justify-between gap-4 px-5 py-4 md:px-8 md:py-5">
        <div className="flex items-center gap-6 min-w-0">
          <Link
            to="/"
            className={cn(
              'font-serif text-xl md:text-2xl text-ink-900 tracking-tight',
              'leading-none',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-accent',
            )}
          >
            Connoisseur
          </Link>
          {account ? <HouseholdSwitcher /> : null}
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/places"
            aria-label="Search places"
            className="hidden md:inline-flex p-2 text-ink-700 hover:text-ink-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-accent"
          >
            <MagnifyingGlass size={20} weight="regular" />
          </Link>
          {account ? (
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button
                  type="button"
                  aria-label="Account menu"
                  className="rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-accent"
                >
                  <Avatar
                    name={account.display_name}
                    email={account.email}
                    size={32}
                  />
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Content align="end">
                <DropdownMenu.Item
                  onSelect={() => navigate('/settings/account')}
                >
                  Your journal account
                </DropdownMenu.Item>
                <DropdownMenu.Item onSelect={() => navigate('/settings')}>
                  Settings
                </DropdownMenu.Item>
                <DropdownMenu.Separator />
                <DropdownMenu.Item onSelect={() => void onLogout()}>
                  <SignOut size={14} weight="regular" aria-hidden />
                  <span>Sign out</span>
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Root>
          ) : null}
        </div>
      </div>
    </header>
  );
}
